// MSQA form history with undo on the phone (row #83, 29 Oct 2026).
//
// A production site visit was lost: both people had signed, a summary answer
// ("Monitor extra visit") was changed by accident, the edit cleared the
// signatures, and the phone's only copy was overwritten. Connor: "we should
// have a history of forms so then the user can go back if they make a
// mistake". So:
//   * every draft save keeps a revision on the server (msqa.py
//     msqa_form_revisions) AND the phone keeps its own last few in the form's
//     IndexedDB record (LocalForm.history), so undo works without signal;
//   * an edit that would clear signatures asks first (Cancel is the default
//     and puts the signed version back), and after a clear the form says
//     plainly what's needed, with an Undo to the signed version;
//   * "History" lists earlier versions in plain words, and "Restore this
//     version" puts back its content and signatures - kept only when the
//     restored content hashes to exactly what they signed.
// Pure functions (no DOM, no IndexedDB) so frontend/tests/msqaHistory.test.ts
// runs in plain Node; msqa.ts does the rendering and storage.

/** Just what the history needs of a signature. */
export interface HistSig {
  role: string;
  content_hash: string | null;
  image?: string;
  /** Row #84: 2 = the form hash leaves the signers out and signer_digest binds this signer's own details; absent = 1. */
  hash_scheme?: number;
  signer_digest?: string | null;
}

/** What signatures are checked against (row #84): the whole content's hash
 * (scheme 1, and a version's identity), the scheme-2 form hash (without the
 * signers' details) and each role's signer digest over it. A bare string is
 * a scheme-1-only fingerprint (versions kept before row #84). */
export interface Fingerprint {
  h1: string | null;
  h2?: string | null;
  digests?: Record<string, string | null>;
}
type FpLike = Fingerprint | string | null | undefined;

function fpOf(fp: FpLike): Fingerprint {
  return typeof fp === "string" || fp == null ? { h1: fp ?? null } : fp;
}

/** One version kept on the phone. */
export interface LocalRev<C = unknown, S extends HistSig = HistSig> {
  at: number;
  /** The whole content's hash (scheme 1): what identifies the version. */
  hash: string;
  /** Row #84: the scheme-2 form hash and signer digests of this version (absent on versions kept before). */
  fp?: Fingerprint;
  content: C;
  signatures: S[];
  /** The server revision it came from, when restored from the server's list. */
  seq?: number | null;
}

export const DEFAULT_SCHEME = 2;

export function sigScheme(s: HistSig): number {
  return s.hash_scheme ?? 1;
}

/** One signature still covers the content with this fingerprint, under its own scheme. */
export function sigValid(s: HistSig, fp: FpLike): boolean {
  const f = fpOf(fp);
  if (sigScheme(s) === 1) return !!f.h1 && s.content_hash === f.h1;
  if (sigScheme(s) !== 2) return false;
  const d = f.digests?.[s.role];
  return !!f.h2 && s.content_hash === f.h2 && !!d && s.signer_digest === d;
}

/** The roles whose signatures no longer cover the content (fingerprint fp). */
export function invalidRoles(sigs: HistSig[], fp: FpLike): string[] {
  return sigs.filter((s) => !sigValid(s, fp)).map((s) => s.role);
}

/** A change on a signed form: to the form itself, or to one signer's own typed details (name, company, position). */
export type SigChange = { kind: "content" } | { kind: "signer"; role: string };

/** Which signatures a change clears (row #84). Form content: every one.
 * A signer's own details: only that signer's - unless the signatures are
 * scheme 1 (signed before row #84), whose hash covers everyone's details. */
export function rolesClearedBy(sigs: HistSig[], change: SigChange): string[] {
  if (change.kind === "content") return sigs.map((s) => s.role);
  return sigs.filter((s) => sigScheme(s) === 1 || s.role === change.role).map((s) => s.role);
}

/** The scheme a new signature is made under: the form's other signatures' (a
 * draft signed under scheme 1 stays scheme 1 until re-signed), else the current one. */
export function signSchemeFor(others: HistSig[]): number {
  return others.length ? sigScheme(others[0]) : DEFAULT_SCHEME;
}

export const PHONE_KEEP_DEFAULT = 8;

/** "A", "A and B", "A, B and C". */
export function joinAnd(xs: string[]): string {
  if (xs.length <= 1) return xs[0] ?? "";
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/** The labels of the signatures held, in the template's order ("Monitor:" -> "Monitor"). */
export function sigLabels(defs: { role: string; label: string }[], sigs: { role: string }[]): string[] {
  const have = new Set(sigs.map((s) => s.role));
  return defs.filter((d) => have.has(d.role)).map((d) => d.label.replace(/:\s*$/, "").trim());
}

/** The labels of the given roles, in the template's order. */
export function roleLabels(defs: { role: string; label: string }[], roles: string[]): string[] {
  return sigLabels(defs, roles.map((role) => ({ role })));
}

/** The question before an edit that would clear signatures. */
export function clearPrompt(labels: string[]): string {
  const n = labels.length;
  return `Changing this clears the ${joinAnd(labels)} signature${n === 1 ? "" : "s"}. Change it anyway?`;
}

/** What the toast and the submit area say after signatures were cleared. */
export function signAgainText(labels: string[]): string {
  return `Sign again: ${labels.join(", ")}. Or undo to the signed version.`;
}

function sigPrint(sigs: HistSig[]): string {
  return sigs.map((s) => `${s.role}:${s.content_hash ?? ""}:${s.signer_digest ?? ""}:${(s.image ?? "").length}:${(s.image ?? "").slice(-24)}`).sort().join("|");
}

/** The signatures are valid for this content: at least one, and every one
 * still covering it under its own scheme (sigValid). */
export function signaturesValidFor(sigs: HistSig[], fp: FpLike): boolean {
  return sigs.length > 0 && sigs.every((s) => sigValid(s, fp));
}

/** Add a version to the phone's list (newest last). Skipped when nothing
 * hashed or signed changed since the newest; keeps the last `keep`, and the
 * newest signed version always survives (it is what Undo goes back to). */
export function pushLocalRev<C, S extends HistSig>(list: LocalRev<C, S>[] | undefined, rev: LocalRev<C, S>, keep = PHONE_KEEP_DEFAULT): LocalRev<C, S>[] {
  const cur = [...(list ?? [])];
  const last = cur[cur.length - 1];
  if (last && last.hash === rev.hash && sigPrint(last.signatures) === sigPrint(rev.signatures)) return cur;
  cur.push(rev);
  if (cur.length <= keep) return cur;
  const signed = latestSigned(cur);
  const tail = cur.slice(-keep);
  return signed && !tail.includes(signed) ? [signed, ...tail.slice(1)] : tail;
}

/** The newest version that had signatures valid for its own content. */
export function latestSigned<C, S extends HistSig>(list: LocalRev<C, S>[] | undefined): LocalRev<C, S> | null {
  for (let i = (list ?? []).length - 1; i >= 0; i--) {
    const r = list![i];
    if (signaturesValidFor(r.signatures, r.fp ?? r.hash)) return r;
  }
  return null;
}

/** The version the current signatures were made over: the newest one that
 * every one of them covers and whose own signatures are valid for it (else
 * the newest signed version at all). */
export function signedVersionFor<C, S extends HistSig>(list: LocalRev<C, S>[] | undefined, sigs: HistSig[]): LocalRev<C, S> | null {
  for (let i = (list ?? []).length - 1; i >= 0; i--) {
    const r = list![i];
    const fp = r.fp ?? r.hash;
    if (sigs.length && sigs.every((s) => sigValid(s, fp)) && signaturesValidFor(r.signatures, fp)) return r;
  }
  return latestSigned(list);
}

export type ClearDecision<C, S extends HistSig> =
  | { kind: "none" }
  | { kind: "put_back"; rev: LocalRev<C, S> }
  | { kind: "clear"; labels: string[]; roles: string[]; undo: LocalRev<C, S> | null };

/** An edit was made on a signed form and the monitor answered the question
 * (go = "Change it anyway"). Cancel puts back the signed version exactly
 * (the answer AND the signatures as they were); going ahead clears the
 * signatures in `roles` (default: all of them; row #84: a signer's own
 * details clear only theirs, rolesClearedBy) and keeps that version for
 * Undo. Nothing to clear: nothing to ask. */
export function decideClear<C, S extends HistSig>(defs: { role: string; label: string }[], sigs: S[],
                                                 history: LocalRev<C, S>[] | undefined, go: boolean,
                                                 roles?: string[]): ClearDecision<C, S> {
  const clear = roles ?? sigs.map((s) => s.role);
  const hit = sigs.filter((s) => clear.includes(s.role));
  if (!hit.length) return { kind: "none" };
  const rev = signedVersionFor(history, sigs);
  if (!go) return rev ? { kind: "put_back", rev } : { kind: "none" };
  return { kind: "clear", labels: sigLabels(defs, hit), roles: hit.map((s) => s.role), undo: rev };
}

// ---------------------------------------------------------------------------
// What changed, in plain words
// ---------------------------------------------------------------------------

interface F { key: string; label?: string; placeholder?: string; type?: string; options?: { key: string; label: string }[] }
interface Sec { key: string; answer: string; pre_fields?: F[]; items: { key: string; label: string }[] }
export interface HistTemplate {
  header?: F[];
  resources?: { key: string; label: string; fields: F[] }[];
  sections: Sec[];
  summary?: { outcome?: { options: { key: string; label: string; short_label?: string }[] };
              non_compliant_action?: { options: { key: string; label: string; short_label?: string }[] }; fields?: F[] };
  comments?: { key: string; label: string };
  signatures?: { role: string; label: string }[];
}
type Obj = Record<string, unknown>;

const STATUS: Record<string, string> = {
  compliant: "Compliant", non_compliant: "Non-compliant", na: "N/A", verified: "Verified", not_verified: "Not verified", Y: "Y", N: "N",
};

function o(v: unknown): Obj {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {};
}
function s(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return JSON.stringify(v);
  return String(v).trim();
}
function lab(f: F): string {
  return (f.label || f.placeholder || f.key).replace(/:\s*$/, "").trim();
}
function shown(f: F, v: string): string {
  if (!v) return "blank";
  if (f.type === "yes_no") return v === "Y" ? "Yes" : v === "N" ? "No" : v;
  return f.options?.find((x) => x.key === v)?.label ?? (v.length > 40 ? `${v.slice(0, 37)}…` : v);
}
function fieldChanges(defs: F[] | undefined, a: Obj, b: Obj, out: string[], prefix = ""): void {
  for (const f of defs ?? []) {
    const x = s(a[f.key]);
    const y = s(b[f.key]);
    if (x === y) continue;
    if (f.type === "textarea" || f.type === "items" || (x.length > 40 || y.length > 40)) out.push(`${prefix}${lab(f)} changed`);
    else out.push(`${prefix}${lab(f)}: ${shown(f, x)} → ${shown(f, y)}`);
  }
}

/** The changes from `before` to `after`, one plain line each, in the form's order. */
export function describeChanges(t: HistTemplate, before: unknown, after: unknown): string[] {
  const A = o(before);
  const B = o(after);
  const out: string[] = [];
  fieldChanges(t.header, o(A.header), o(B.header), out);
  for (const g of t.resources ?? []) fieldChanges(g.fields, o(o(A.resources)[g.key]), o(o(B.resources)[g.key]), out, `${g.label}: `);
  for (const sec of t.sections) {
    fieldChanges(sec.pre_fields, o(A.pre), o(B.pre), out);
    for (const it of sec.items) {
      const x = o(o(A.answers)[it.key]);
      const y = o(o(B.answers)[it.key]);
      const name = `${it.key} ${it.label}`;
      if (s(x.status) !== s(y.status)) out.push(`${name}: ${STATUS[s(x.status)] ?? (s(x.status) || "blank")} → ${STATUS[s(y.status)] ?? (s(y.status) || "blank")}`);
      if (s(x.comment) !== s(y.comment)) out.push(`${it.key} comment ${s(y.comment) ? (s(x.comment) ? "changed" : "added") : "removed"}`);
      const px = Array.isArray(x.photos) ? x.photos.length : 0;
      const py = Array.isArray(y.photos) ? y.photos.length : 0;
      if (px !== py) out.push(`${it.key}: ${py > px ? `${py - px} photo${py - px === 1 ? "" : "s"} added` : `${px - py} photo${px - py === 1 ? "" : "s"} removed`}`);
      else if (JSON.stringify(x.photos ?? []) !== JSON.stringify(y.photos ?? [])) out.push(`${it.key} photo captions changed`);
      for (const k of ["initials", "date", "document_id"]) if (s(x[k]) !== s(y[k])) out.push(`${it.key} ${k === "document_id" ? "document" : k} changed`);
    }
  }
  const SA = o(A.summary);
  const SB = o(B.summary);
  const opt = (opts: { key: string; label: string; short_label?: string }[] | undefined, v: string) =>
    v ? (opts?.find((x) => x.key === v)?.short_label ?? opts?.find((x) => x.key === v)?.label ?? v) : "blank";
  if (t.summary?.outcome && s(SA.outcome) !== s(SB.outcome)) out.push(`Outcome: ${opt(t.summary.outcome.options, s(SA.outcome))} → ${opt(t.summary.outcome.options, s(SB.outcome))}`);
  if (t.summary?.non_compliant_action && s(SA.non_compliant_action) !== s(SB.non_compliant_action)) {
    out.push(`Next step: ${opt(t.summary.non_compliant_action.options, s(SA.non_compliant_action))} → ${opt(t.summary.non_compliant_action.options, s(SB.non_compliant_action))}`);
  }
  fieldChanges(t.summary?.fields, SA, SB, out);
  if (t.comments && s(SA[t.comments.key]) !== s(SB[t.comments.key])) out.push(`${t.comments.label.replace(/:\s*$/, "")} changed`);
  for (const sg of t.signatures ?? []) {
    const x = o(o(A.signers)[sg.role]);
    const y = o(o(B.signers)[sg.role]);
    if (JSON.stringify(x) !== JSON.stringify(y)) out.push(`${sg.label.replace(/:\s*$/, "")}'s details changed`);
  }
  return out;
}

/** One line for a history row. */
export function summariseChanges(lines: string[], first: boolean): string {
  if (first) return "First saved version";
  if (!lines.length) return "No change to the answers";
  if (lines.length <= 2) return lines.join("; ");
  return `${lines.slice(0, 2).join("; ")} and ${lines.length - 2} more`;
}

/** A version that changed only the signatures (labels, in the template's order). */
export function signatureChange(before: string[], after: string[]): string | null {
  const added = after.filter((x) => !before.includes(x));
  const gone = before.filter((x) => !after.includes(x));
  if (added.length) return `Signed by ${joinAnd(added)}`;
  if (gone.length) return `${joinAnd(gone)} signature${gone.length === 1 ? "" : "s"} cleared`;
  return null;
}

/** A history row's "signed or not" words. */
export function signedWords(labels: string[]): string {
  return labels.length ? `Signed: ${labels.join(", ")}` : "Not signed";
}
